// UBB-341: restore the Loop subscribe widget inside Motto's <quick-add-modal>.
//
// TWO independent faults, both confirmed against the live theme (160698564834):
//
//  1. WHY IT WAS ABSENT. Loop resolves which widget to render by TEMPLATE NAME. The app block
//     bootstrap emits `window.LOOP_WIDGET[id].templateName` from the current template suffix:
//     null on the PDP (-> Loop's default product widget), but "quickview" on our
//     templates/product.quickview.json. Loop has no widget configured for a "quickview" template,
//     so startLoopWidget() bails and HIDES the container. Loop's own console says so:
//         Loop Widget: Error loading ... No widget found for template: quickview
//         Loop Widget: Hiding widget from: startLoopWidget
//     The container is left as `.loop-widget-container.loop-display-none`, which
//     _loop-widget.scss holds open at 288px under `.product-info__purchase--has-plans` — that
//     288px void IS the empty gap reported on the ticket.
//
//     Fix: normalise templateName back to null before Loop reads it. Intercepting the
//     `window.LOOP_WIDGET` ASSIGNMENT (rather than patching after the fact) means Loop's very
//     first startLoopWidget() call already succeeds — no failed ~860ms render, no visible flash.
//     On the PDP templateName is already null, so this is a no-op there.
//
//     The alternative fix is app-side: configure a Loop widget for the quickview template in the
//     Loop admin. Left as a theme fix so it can't silently regress on an app-settings change.
//
//  2. WHY IT WOULD HAVE BEEN UNSTYLED. loop-widget.js (the brand restyle: benefits grid, custom
//     check-circles, UBB-268 combined saving %) ships in product.autojs.js, which
//     layout/theme.liquid loads only when `request.page_type == 'product'`. Quick-view opens from
//     collection / search / home, so it never ran there. Importing it here puts it in
//     global.autojs.js. Its #loop-product-data feed also had to move INSIDE the section to survive
//     quick-add.js's inject — see the note in sections/main-product.liquid.
//
// Vendored quick-add.js is untouched (house rule: override from src/).
import loopWidget from './loop-widget.js';

// loopWidget() must run at most once per page — see the note in watchModal() for why calling it
// again froze the tab. Module scope, so it is shared across every modal on the page.
let brandStyleStarted = false;
// Single pending "did Loop fail to hydrate?" check, replaced rather than stacked.
let recoverTimer = 0;

// Loop's config is keyed by product id; every entry must resolve to the default product widget.
function normaliseTemplateName(config) {
  if (!config || typeof config !== 'object') return;
  Object.values(config).forEach((entry) => {
    if (entry && typeof entry === 'object' && entry.templateName) {
      entry.templateName = null;
    }
  });
}

// Intercept the assignment itself. Loop's bootstrap does `window.LOOP_WIDGET = {...}` and then
// immediately polls for LoopSubscriptions, so normalising in the setter lands before any read.
function interceptLoopConfig() {
  let stored = window.LOOP_WIDGET;
  normaliseTemplateName(stored);

  try {
    Object.defineProperty(window, 'LOOP_WIDGET', {
      configurable: true,
      enumerable: true,
      get() {
        return stored;
      },
      set(value) {
        normaliseTemplateName(value);
        stored = value;
      },
    });
  } catch {
    // Non-configurable already (another script got there first) — the modal fallback covers it.
  }
}

// Belt-and-braces: if the widget still ends up hidden and empty after the modal injects (Loop
// redefines its own globals late, and the interception above can be lost to that), null the
// template name and re-render. This is the slow path — it only runs when the fast path failed.
function recoverIfHidden(modalContent) {
  const container = modalContent.querySelector('.loop-widget-container');
  if (!container) return;
  if (!container.classList.contains('loop-display-none')) return;
  if (container.children.length > 0) return;

  // No selling plans (gift cards) => Loop keeps the container hidden BY DESIGN and
  // main-product.liquid omits the --has-plans modifier. Re-rendering would just log a Loop error.
  if (!container.closest('.product-info__purchase--has-plans')) return;

  const config = window.LOOP_WIDGET;
  const loop = window.LoopSubscriptions;
  if (!config || !loop || typeof loop.startLoopWidget !== 'function') return;

  normaliseTemplateName(config);
  Object.keys(config).forEach((productId) => {
    try {
      loop.startLoopWidget(productId);
    } catch {
      // Loop already logs its own failures — nothing useful to add here.
    }
  });
}

// Watch one modal's inject target. quick-add.js sets modalContent.innerHTML on open and clears it
// on hide(), so childList on that node is the open/close signal — no vendored event exists.
function watchModal(modal) {
  const content = modal.querySelector('[id^="QuickAddInfo-"]');
  if (!content || content.dataset.loopQuickviewWatched === 'true') return;
  content.dataset.loopQuickviewWatched = 'true';

  const observer = new MutationObserver(() => {
    if (!content.children.length) return; // hide() emptied it

    // Brand restyle — ONCE PER PAGE. loopWidget() is NOT idempotent: every call installs another
    // MutationObserver (on document.body here, since the modal carries no `section[id^=MainProduct-]`)
    // and stacks another wrapper around LoopSubscriptions.startLoopWidget. Nothing disconnects the
    // previous ones.
    //
    // Calling it per mutation froze the tab on the SECOND quick-view open. Each observer
    // self-disconnects around its own inject(), but the others stay connected and see those
    // mutations — and inject() unconditionally rewrites the badge / compare-at textContent, so every
    // pass guarantees a fresh mutation. Two or more observers therefore feed each other in a
    // cascade that never settles and blocks the main thread.
    //
    // One observer is enough for the whole page: it watches document.body subtree, so it restyles
    // the widget in every later modal open too.
    if (!brandStyleStarted) {
      brandStyleStarted = true;
      loopWidget();
    }

    // Loop hydrates asynchronously; give it a beat before deciding it failed. Replace the pending
    // check rather than stacking one per mutation.
    window.clearTimeout(recoverTimer);
    recoverTimer = window.setTimeout(() => recoverIfHidden(content), 1200);
  });
  // childList only (not subtree): loopWidget() writes to DESCENDANTS of `content`, so it can
  // never retrigger this callback.
  observer.observe(content, { childList: true });
}

function loopWidgetQuickview() {
  interceptLoopConfig();

  // Capture phase: attach the observer before ModalOpener's own click handler starts the fetch.
  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest?.('modal-opener[data-modal]');
      if (!opener) return;

      const selector = opener.getAttribute('data-modal');
      if (!selector) return;

      let modal;
      try {
        modal = document.querySelector(selector);
      } catch {
        return; // malformed selector — nothing to do
      }
      if (!modal || modal.tagName !== 'QUICK-ADD-MODAL') return;

      // UBB-447: Loop reads the quantity with a page-wide document.querySelector('.quantity__input')
      // (loop_widget.js loopWidgetGetQuantity, not scoped to its product form). Motto's ModalDialog
      // appends every modal to the END of <body>, after the cart drawer, so with items in the cart
      // Loop priced the quick view off the first cart line's qty. Moving the modal to the START of
      // <body> makes its stepper the first match. It's position:fixed, so DOM order is visual-only
      // for equal z-index (the cart drawer, later in the DOM, still paints above it). ModalDialog's
      // `moved` flag makes its connectedCallback a no-op on this re-insert.
      if (document.body.firstElementChild !== modal)
        document.body.prepend(modal);
      watchModal(modal);
    },
    true
  );
}

loopWidgetQuickview();

export default loopWidgetQuickview;
